import { Stack } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActivateTenant } from '@/backoffice/features/tenants/components/activate-tenant/activate-tenant';
import { ManageFeatureFlags } from '@/backoffice/features/tenants/components/feature-flags/manage-feature-flags';
import { TermsAcceptanceChip } from '@/backoffice/features/tenants/components/terms-acceptance-chip/terms-acceptance-chip';
import { UpdateTenant } from '@/backoffice/features/tenants/components/update-tenant/update-tenant';
import { TTransformedTenant } from '@/backoffice/features/tenants/hooks/use-tenants-transformer/use-tenants-transformer.types';
import { getSubscriptionStatus } from '@/backoffice/features/tenants/utils/subscription-status';
import { SUBSCRIPTION_STATUS_CHIP } from '@/backoffice/features/tenants/utils/subscription-status.const';
import { MoreHorizIcon } from '@/shared/assets/icons/more-horiz-icon';
import { TenantIcon } from '@/shared/assets/icons/tenant-icon';
import { Avatar } from '@/shared/components/ui/avatar/avatar';
import { TUserInfo } from '@/shared/components/ui/avatar/avatar.types';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import { Chip } from '@/shared/components/ui/chip/chip';
import { CopyIdButton } from '@/shared/components/ui/copy-id-button/copy-id-button';
import { createNameRenderer } from '@/shared/components/ui/data-table/data-table.renderer';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { Skeleton } from '@/shared/components/ui/skeleton/skeleton.styled';
import { Tag } from '@/shared/components/ui/tag/tag';
import { useBillingEntities } from '@/shared/features/billing/hooks/use-billing-entities/use-billing-entities';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';

export const TenantNameRenderer = createNameRenderer(
  TenantIcon,
  { size: '1.25rem' },
  'companyName',
);

export const CreationDateRenderer: React.FC<ICellRendererParams> = ({
  data,
}) => {
  const { formatLocalTime } = useDateUtils();
  return formatLocalTime(data.creationDate);
};

export const OwnerRenderer: React.FC<ICellRendererParams> = ({ data }) => {
  return <Avatar user={data.owner as TUserInfo} />;
};

export const TermsRenderer: React.FC<ICellRendererParams> = ({ data }) => {
  return (
    <TermsAcceptanceChip
      termsAndConditions={data.raw?.owner?.termsAndConditions}
    />
  );
};

export const TenantPlanRenderer: React.FC<
  ICellRendererParams<TTransformedTenant>
> = ({ data }) => {
  const { getTierLabel } = useBillingEntities();

  if (!data) return null;
  if (data.subscription === undefined) return <Skeleton />;
  if (!data.subscription) return '-';

  return <Tag label={getTierLabel(data.subscription.tier)} />;
};

export const TenantSubscriptionStatusRenderer: React.FC<
  ICellRendererParams<TTransformedTenant>
> = ({ data }) => {
  const { t } = useTranslation();

  if (!data) return null;
  if (data.subscription === undefined) return <Skeleton />;
  if (!data.subscription) return '-';

  const { status, labelKey } =
    SUBSCRIPTION_STATUS_CHIP[getSubscriptionStatus(data.subscription)];
  return <Chip status={status} label={t(labelKey)} />;
};

// Term boundaries are whole days stored in UTC, rendered date-only in UTC as on the subscription page.
export const TenantSubscriptionStartRenderer: React.FC<
  ICellRendererParams<TTransformedTenant>
> = ({ data }) => {
  const { formatLocalTime, dateTimeFormat } = useDateUtils();

  if (!data) return null;
  if (data.subscription === undefined) return <Skeleton />;

  const startTs = data.subscription?.term.startTs;
  return startTs
    ? formatLocalTime(startTs, 'UTC', dateTimeFormat.split(' ')[0])
    : '-';
};

export const TenantSubscriptionEndRenderer: React.FC<
  ICellRendererParams<TTransformedTenant>
> = ({ data }) => {
  const { t } = useTranslation();
  const { formatLocalTime, dateTimeFormat } = useDateUtils();
  const dateOnlyFormat = dateTimeFormat.split(' ')[0];

  if (!data) return null;
  if (data.subscription === undefined) return <Skeleton />;
  if (!data.subscription) return '-';

  const { cancellationTs, expirationTs } = data.subscription.term;

  if (cancellationTs) {
    return formatLocalTime(cancellationTs, undefined, dateOnlyFormat);
  }
  if (expirationTs) {
    return formatLocalTime(expirationTs, 'UTC', dateOnlyFormat);
  }
  return t('backoffice:open_ended');
};

export const TenantContextMenuRenderer: React.FC<ICellRendererParams> = ({
  data,
}) => {
  const { t } = useTranslation();
  const isActive = data.isActive;

  return (
    <Stack
      direction="row"
      gap="1rem"
      alignItems="center"
      onClick={(e) => e.stopPropagation()}
    >
      <Chip
        status={isActive ? 'green' : 'grey'}
        label={isActive ? t('common:active') : t('common:inactive')}
      />
      <PopoverController
        trigger={
          <IconButton>
            <MoreHorizIcon />
          </IconButton>
        }
      >
        {(closePopover) => (
          <>
            <CopyIdButton
              id={data.tenantId}
              component="MenuItem"
              tooltipProps={{ placement: 'left' }}
            />
            {!isActive && (
              <ActivateTenant
                onSuccess={closePopover}
                tenantId={data.tenantId}
              />
            )}
            <UpdateTenant tenantId={data.tenantId} onClose={closePopover} />
            <ManageFeatureFlags
              tenantId={data.tenantId}
              onClose={closePopover}
            />
          </>
        )}
      </PopoverController>
    </Stack>
  );
};
